'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Media } from '@/components/Media'
import { cn } from '@/lib/utils'
import type { Media as MediaType } from '@/payload-types'

export interface ProductGalleryItem {
  id: string
  title: string
  shortDescription: string
  image: string | MediaType | null
  href: string
  slug?: string
}

export interface ProductGalleryProps {
  products: ProductGalleryItem[]
  className?: string
  /** Tailwind height class for desktop row. Default: h-[28rem] */
  heightClass?: string
  sourceBlock?: string
}

export default function ProductGallery({
  products,
  className,
  heightClass = 'h-[28rem]',
  sourceBlock,
}: ProductGalleryProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (!products || products.length === 0) return null

  // Each card can expand to flex-grow: 1.7 on hover while siblings stay at 1.
  // Hovered width = 1.7 / (N - 1 + 1.7) of the flex container.
  // We use a `sizes` upper bound covering the hovered case to avoid pixelation.
  const hoveredFraction = 1.7 / (products.length - 1 + 1.7)
  const desktopVw = Math.ceil(hoveredFraction * 100)
  const desktopSizes = `(max-width: 1024px) 50vw, ${desktopVw}vw`

  return (
    <div className={cn('w-full', className)}>
      {/* Desktop: flex-grow hover animation */}
      <div
        className={cn('hidden md:flex gap-4', heightClass)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {products.map((product, index) => {
          const isHovered = hoveredIndex === index
          const growUnits = isHovered ? 1.7 : 1
          const mediaResource =
            typeof product.image === 'object' && product.image !== null ? product.image : null

          return (
            <Link
              key={product.id}
              href={product.href}
              className="group relative overflow-hidden h-full min-w-0 transition-all duration-500 ease-in-out"
              style={{ flexGrow: growUnits, flexBasis: 0 }}
              onMouseEnter={() => setHoveredIndex(index)}
              data-card-type="product"
              data-card-slug={product.slug}
              data-card-block={sourceBlock}
            >
              {/* Product image — scale applied to wrapper so it doesn't conflict
                 with the Media component's opacity transition on the <img> */}
              <div
                className={cn(
                  'absolute inset-0 transition-transform duration-400 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]',
                  isHovered && 'scale-105',
                )}
              >
                <Media
                  resource={mediaResource}
                  alt={mediaResource?.alt || product.title}
                  fill
                  pictureClassName="absolute inset-0 w-full h-full"
                  imgClassName="object-cover"
                  size={desktopSizes}
                />
              </div>

              {/* Gradient overlay — stronger on hover */}
              <div
                className={cn(
                  'absolute inset-0 z-10 transition-opacity duration-500',
                  'bg-gradient-to-t from-black/80 via-black/20 to-transparent',
                  isHovered ? 'opacity-100' : 'opacity-60',
                )}
              />

              {/* Content overlay — anchored to bottom, grows upward */}
              <div className="absolute bottom-0 left-0 right-0 z-20 p-5 flex flex-col justify-end">
                {/* Description — expands upward above the title */}
                <div
                  className={cn(
                    'overflow-hidden transition-all ease-out',
                    isHovered
                      ? 'max-h-28 opacity-100 mb-2 duration-500 delay-400'
                      : 'max-h-0 opacity-0 mb-0 duration-300 delay-0',
                  )}
                >
                  <p className="text-white/80 text-sm leading-relaxed">{product.shortDescription}</p>
                </div>

                <div className="flex items-center justify-between">
                  <h3 className="title-lg text-white uppercase">{product.title}</h3>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className={cn(
                      'text-white shrink-0 transition-transform duration-300',
                      isHovered && 'translate-x-1',
                    )}
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Mobile: 2-column grid */}
      <div className="md:hidden grid grid-cols-2 gap-3">
        {products.map((product, index) => {
          const mediaResource =
            typeof product.image === 'object' && product.image !== null ? product.image : null

          return (
            <Link
              key={product.id}
              href={product.href}
              className="group relative block aspect-[3/4] w-full overflow-hidden"
              data-card-type="product"
              data-card-slug={product.slug}
              data-card-block={sourceBlock}
            >
              <div className="absolute inset-0">
                <Media
                  resource={mediaResource}
                  alt={mediaResource?.alt || product.title}
                  fill
                  pictureClassName="absolute inset-0 w-full h-full"
                  imgClassName="object-cover"
                  size="50vw"
                  priority={index < 2}
                />
              </div>

              <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80" />

              <div className="absolute bottom-0 left-0 right-0 z-20 p-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white uppercase">{product.title}</h3>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-white shrink-0"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-white/70 text-xs mt-1 line-clamp-2">
                  {product.shortDescription}
                </p>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
